import React, { useState, useEffect, useRef } from 'react';
import apiClient from '../utils/apiClient';
import server from '../utils/serverConfig';
import { UploadCloud, FileCode, Play, Download, AlertCircle, CheckCircle, Loader2, X, RefreshCw, Trash2 } from 'lucide-react';
import { useBacktestStore } from '../contexts/LTPStore/useBacktestStore';

export const BacktestView = () => {
    // Local state for file input (transient)
    const [file, setFile] = useState(null);
    const fileInputRef = useRef(null);
    const [isRefreshing, setIsRefreshing] = useState(false);

    // Global Store State
    const {
        jobId,
        status,
        progress,
        stage,
        error,
        lastUpdated,

        setJobId,
        setStatus,
        setProgress,
        setStage,
        setError,
        setLastUpdated,

        resetState,
        updateState
    } = useBacktestStore();

    const [jobs, setJobs] = useState([]);
    const [jobToDelete, setJobToDelete] = useState(null); // State for delete confirmation modal

    const BACKTEST_API_URL = "https://backtest.sypto.xyz";

    const formatTime = (dateStr) => {
        if (!dateStr) return '';
        // If string doesn't end with Z, append it to treat as UTC
        const d = new Date(dateStr.endsWith('Z') ? dateStr : dateStr + 'Z');
        return d.toLocaleTimeString();
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return '-';
        const d = new Date(dateStr.endsWith('Z') ? dateStr : dateStr + 'Z');
        return d.toLocaleString([], { dateStyle: 'short', timeStyle: 'short' });
    };

    const fetchJobs = async () => {
        try {
            const res = await apiClient.get(`${BACKTEST_API_URL}/jobs?job_type=backtest`);
            // Filter out audit jobs locally as well (double safety)
            const sortedJobs = res.data.filter(j => j.job_type !== 'audit');
            setJobs(sortedJobs);

            // Auto-resume logic:
            // If we are IDLE (no current job) and the latest job is active, resume it.
            // Also if the latest job is DONE/FAILED, we might want to show it? 
            // The user request implies "my data stay there", so showing the latest status is good.
            if (!jobId && sortedJobs.length > 0) {
                const latest = sortedJobs[0];
                if (['QUEUED', 'UPLOADING', 'UNZIPPING', 'PROCESSING'].includes(latest.status)) {
                    // Resume active job
                    updateState({
                        jobId: latest.id,
                        status: latest.status,
                        progress: latest.progress,
                        stage: latest.message, // Map message to stage for display
                        error: null,
                        lastUpdated: formatTime(latest.updated_at)
                    });
                } else if (['COMPLETED', 'FAILED', 'CANCELLED'].includes(latest.status)) {
                    // Optionally show the last finished job status too? 
                    // User said "on restart my data stay there". 
                    // So yes, let's load the latest job state even if finished.
                    // But we MUST be careful not to overwrite if user just hit "Start New" (Reset).
                    // Actually, "Reset" clears jobId. So if we reload and see a job, should we show it?
                    // Yes, on a fresh page load (jobId is null).

                    // But wait, if they hit "Start New" -> jobId null -> this hook runs -> fetches jobs -> sees latest is DONE -> auto-loads it?
                    // That would prevent starting a new one!
                    // We need a flag or only do this ON MOUNT.
                    // Since fetchJobs is called on mount, this is safer.
                    // But fetchJobs is ALSO called by the poll interval or manual refresh? No, pollStatus is different.
                    // fetchJobs is called on: useEffect mount, and when status changes to DONE/FAILED.
                }
            }
        } catch (err) {
            console.error("Failed to fetch jobs", err);
        }
    };

    // Auto-resume on MOUNT only (via fetchJobs side effect)
    useEffect(() => {
        const init = async () => {
            try {
                const res = await apiClient.get(`${BACKTEST_API_URL}/jobs?job_type=backtest`);
                // Filter audit jobs
                const sortedJobs = res.data.filter(j => j.job_type !== 'audit');
                setJobs(sortedJobs);

                // Only on initial load (when jobId is null), check for latest job to display.
                if (sortedJobs.length > 0) {
                    const latest = sortedJobs[0];
                    // Only resume if it's active OR if it's the very first load and we want to show persistence.
                    // A safe bet for "persistence" is: always load the latest job state on page refresh.
                    updateState({
                        jobId: latest.id,
                        status: latest.status === 'COMPLETED' ? 'DONE' : latest.status, // Map COMPLETED -> DONE for frontend
                        progress: latest.progress,
                        stage: latest.message,
                        error: latest.status === 'FAILED' ? latest.message : null,
                        lastUpdated: formatTime(latest.updated_at)
                    });
                }
            } catch (e) { console.error(e); }
        };
        init();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Run ONCE on mount

    // Separate fetchJobs for manual refresh / history updates without auto-selecting
    const refreshHistory = async () => {
        try {
            const res = await apiClient.get(`${BACKTEST_API_URL}/jobs?job_type=backtest`);
            setJobs(res.data.filter(j => j.job_type !== 'audit'));
        } catch (e) { console.error(e); }
    };

    // Also refresh list when job status changes to DONE
    useEffect(() => {
        if (status === 'DONE' || status === 'FAILED') {
            fetchJobs();
        }
    }, [status]);

    const pollStatus = React.useCallback(async () => {
        if (!jobId) return;
        try {
            const res = await apiClient.get(`${BACKTEST_API_URL}/job/${jobId}`);
            const data = res.data;

            // Safety Check: If we somehow picked up an audit job (e.g. from local storage or mixed state),
            // we must NOT display it in the Backtest View. Reset state immediately.
            if (data.job_type === 'audit') {
                console.warn("Detected Audit job in Backtest view. Resetting state.");
                setJobId(null);
                setStatus('IDLE');
                resetState();
                return;
            }

            setProgress(data.progress);
            // Derive stage from status/message if not explicit
            setStage(data.message || data.status);
            setLastUpdated(new Date().toLocaleTimeString());

            if (data.status === 'COMPLETED') {
                setStatus('DONE');
                setProgress(100);
            } else if (data.status === 'FAILED') {
                setStatus('FAILED');
                setError(data.message || 'Backtest failed');
            } else {
                // Determine active status mapping
                if (data.status === 'QUEUED' || data.status === 'UPLOADING') setStatus('UPLOADING');
                else setStatus('PROCESSING');
            }
        } catch (err) {
            console.error("Poll error", err);
            // If job not found (404), maybe it was deleted? Reset state.
            if (err.response && err.response.status === 404) {
                setJobId(null);
                setStatus('IDLE');
                resetState();
            }
        }
    }, [jobId]);

    // Manual Refresh Function (with loading indicator)
    const fetchStatus = async () => {
        setIsRefreshing(true);
        await pollStatus();
        setIsRefreshing(false);
    };

    // Auto-refresh poll every 3 seconds when active
    useEffect(() => {
        let interval;
        if ((status === 'PROCESSING' || status === 'UPLOADING') && jobId) {
            pollStatus(); // Immediate fetch
            interval = setInterval(pollStatus, 3000);
        }
        return () => clearInterval(interval);
    }, [status, jobId, pollStatus]);

    const handleFileSelect = (e) => {
        if (e.target.files && e.target.files[0]) {
            const selectedFile = e.target.files[0];
            if (selectedFile.name.endsWith('.zip')) {
                setFile(selectedFile);
                setError(null);
                // Reset global state for new job
                resetState();
            } else {
                setError('Please select a .zip file');
            }
        }
    };

    const handleStartBacktest = async () => {
        if (!file) return;

        try {
            setStatus('UPLOADING');
            setError(null);

            // 1. Upload File Directly
            const formData = new FormData();
            formData.append('file', file);

            const res = await apiClient.post(`${BACKTEST_API_URL}/upload`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            const { job_id, status: initialStatus } = res.data;
            setJobId(job_id);

            // 2. Move to Processing state
            setStatus('PROCESSING');
            setStage('Upload Complete. Starting...');
            fetchStatus(); // Initial fetch

        } catch (err) {
            console.error(err);
            setStatus('FAILED');
            setError(err.response?.data?.detail || err.message || 'Failed to start backtest');
        }
    };

    const handleCancel = async () => {
        if (!jobId) return;
        try {
            await apiClient.post(`${BACKTEST_API_URL}/job/${jobId}/cancel`);
            setStatus('CANCELLED');
            setError('Result cancelled by user');
        } catch (err) {
            console.error("Cancel failed", err);
        }
    };

    const handleDeleteJob = (id, e) => {
        e.stopPropagation(); // Prevent row click
        setJobToDelete(id);
    };

    const confirmDelete = async () => {
        if (!jobToDelete) return;

        const id = jobToDelete;
        try {
            await apiClient.delete(`${BACKTEST_API_URL}/job/${id}`);
            // If deleting the currently viewed job, reset state
            if (id === jobId) {
                handleReset();
            }
            fetchJobs(); // Refresh list
            setJobToDelete(null); // Close modal
        } catch (err) {
            console.error("Delete failed", err);
            const status = err.response?.status;
            const detail = err.response?.data?.detail;
            setError(`Failed to delete job (${status || 'Unknown Error'}): ${detail || err.message}`);
            setJobToDelete(null); // Close modal on error too? Or keep it open? Better close to show error.
        }
    };

    const handleReset = () => {
        setFile(null);
        resetState();
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleDownload = async () => {
        if (!jobId || status !== 'DONE') return;

        try {
            // Direct download link
            const downloadUrl = `${BACKTEST_API_URL}/job/${jobId}/result`;
            window.location.href = downloadUrl;
        } catch (e) {
            console.error("Download fail", e);
        }
    };

    return (
        <div className="flex flex-col h-full bg-[#0f1116] text-gray-100 overflow-hidden relative">

            {/* Top Toolbar / Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800 bg-[#1a1d29]/80 backdrop-blur">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-600/20 rounded-lg">
                        <FileCode className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold tracking-tight text-white">Backtest Manager</h1>
                        <p className="text-xs text-gray-500 font-medium">Upload strategies and analyze performance</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <div className="px-3 py-1 bg-blue-500/10 rounded-full border border-blue-500/20 text-xs font-medium text-blue-400">
                        v2.0.1
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-hidden p-6">
                <div className="grid grid-cols-12 gap-6 h-full">

                    {/* LEFT PANEL: Controls & Current Status (4 cols) */}
                    <div className="col-span-12 lg:col-span-4 flex flex-col gap-6 h-full overflow-y-auto pr-2 custom-scrollbar">

                        {/* 1. Upload Card - Only show when IDLE */}
                        {status === 'IDLE' && (
                            <div className="bg-[#1a1d29] rounded-2xl border border-gray-800 p-1 shadow-xl flex flex-col relative overflow-hidden group">
                                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />

                                <div className="p-5">
                                    <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                                        <UploadCloud className="w-4 h-4" /> New Backtest
                                    </h3>

                                    {!file ? (
                                        <div className="border-2 border-dashed border-gray-700 hover:border-blue-500/50 rounded-xl p-8 transition-all duration-300 text-center relative bg-[#0f1116]/50">
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                accept=".zip"
                                                onChange={handleFileSelect}
                                                className="hidden"
                                                id="file-upload"
                                            />
                                            <label
                                                htmlFor="file-upload"
                                                className="cursor-pointer flex flex-col items-center gap-3"
                                            >
                                                <div className="w-14 h-14 bg-gray-800 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                                                    <UploadCloud className="w-6 h-6 text-blue-400" />
                                                </div>
                                                <div>
                                                    <span className="text-blue-400 font-semibold hover:underline">Click to upload</span>
                                                    <span className="text-gray-500 text-sm"> or drag zip file</span>
                                                </div>
                                                <p className="text-xs text-gray-600 mt-2">Max size: 100MB</p>
                                            </label>
                                        </div>
                                    ) : (
                                        <div className="bg-[#0f1116] rounded-xl border border-gray-700 p-4 relative overflow-hidden">
                                            <div className="flex items-start justify-between">
                                                <div className="flex items-center gap-3 overflow-hidden">
                                                    <div className="bg-blue-500/20 p-2.5 rounded-lg shrink-0">
                                                        <FileCode className="text-blue-400 w-5 h-5" />
                                                    </div>
                                                    <div className="min-w-0">
                                                        <p className="text-sm font-medium text-white truncate">{file.name}</p>
                                                        <p className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                                                    </div>
                                                </div>
                                                <button onClick={handleReset} className="text-gray-400 hover:text-red-400 hover:bg-red-400/10 p-1.5 rounded-lg transition-colors">
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>

                                            <button
                                                onClick={handleStartBacktest}
                                                className="mt-4 w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white py-2.5 rounded-lg font-semibold shadow-lg shadow-blue-900/20 transition-all active:scale-[0.98]"
                                            >
                                                <Play className="w-4 h-4 fill-current" />
                                                Initialize Backtest
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* 2. Active Job Status (Conditional) */}
                        {status !== 'IDLE' && (
                            <div className="bg-[#1a1d29] rounded-2xl border border-gray-800 p-1 shadow-xl flex flex-col animate-in fade-in slide-in-from-bottom-4 duration-500">
                                <div className="p-5 space-y-5">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                                            <Loader2 className={`w-4 h-4 ${status === 'PROCESSING' || status === 'UPLOADING' ? 'animate-spin text-blue-400' : ''}`} />
                                            Current Status
                                        </h3>
                                        {status === 'DONE' && <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs font-bold rounded border border-emerald-500/20">COMPLETED</span>}
                                        {status === 'FAILED' && <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs font-bold rounded border border-red-500/20">FAILED</span>}
                                        {status === 'CANCELLED' && <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs font-bold rounded border border-yellow-500/20">CANCELLED</span>}
                                    </div>

                                    {/* Progress Radial or Bar */}
                                    <div className="flex flex-col items-center py-2">
                                        {status === 'DONE' ? (
                                            <div className="w-20 h-20 bg-emerald-500/10 rounded-full flex items-center justify-center border-4 border-emerald-500/20 mb-3 relative">
                                                <div className="absolute inset-0 rounded-full border-4 border-emerald-500 opacity-20 animate-ping"></div>
                                                <CheckCircle className="w-8 h-8 text-emerald-500" />
                                            </div>
                                        ) : (status === 'FAILED' || status === 'CANCELLED') ? (
                                            <div className="flex flex-col items-center w-full">
                                                <div className={`w-20 h-20 rounded-full flex items-center justify-center border-4 mb-3 ${status === 'FAILED' ? 'bg-red-500/10 border-red-500/20' : 'bg-yellow-500/10 border-yellow-500/20'}`}>
                                                    <X className={`w-8 h-8 ${status === 'FAILED' ? 'text-red-500' : 'text-yellow-500'}`} />
                                                </div>

                                                {/* Controls for Failed/Cancelled State */}
                                                <div className="text-center mt-2">
                                                    <button
                                                        onClick={handleReset}
                                                        className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm font-medium transition-colors border border-gray-700 hover:border-gray-600"
                                                    >
                                                        Start New Backtest
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="w-full space-y-4">
                                                <div className="flex justify-between text-xs font-medium text-gray-400">
                                                    <span>Progress</span>
                                                    <span className="text-blue-400">{progress}%</span>
                                                </div>
                                                <div className="h-2.5 bg-gray-800 rounded-full overflow-hidden border border-gray-700/50">
                                                    <div
                                                        className="h-full bg-gradient-to-r from-blue-600 to-purple-500 transition-all duration-500 relative"
                                                        style={{ width: `${progress}%` }}
                                                    >
                                                        <div className="absolute inset-0 bg-white/20 animate-[shimmer_2s_infinite]"></div>
                                                    </div>
                                                </div>
                                                <div className="text-center flex justify-center gap-4 mt-2">
                                                    <button
                                                        onClick={handleCancel}
                                                        className="text-xs font-semibold text-red-500 hover:text-red-400 transition-colors uppercase tracking-wide border border-red-500/30 px-3 py-1 rounded hover:bg-red-500/10"
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        onClick={handleReset}
                                                        className="text-xs text-gray-500 hover:text-white transition-colors underline"
                                                    >
                                                        Start New
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        <p className="mt-4 text-center text-sm font-medium text-white">
                                            {stage || error || "Initializing..."}
                                        </p>
                                        {lastUpdated && <p className="text-[10px] text-gray-500 mt-1">Updated: {lastUpdated}</p>}
                                    </div>

                                    {status === 'DONE' && (
                                        <div className="grid grid-cols-2 gap-3 pt-2">
                                            <button
                                                onClick={handleDownload}
                                                className="col-span-2 flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white py-2.5 rounded-lg font-medium transition-colors"
                                            >
                                                <Download className="w-4 h-4" /> Download Result
                                            </button>
                                            <button
                                                onClick={handleReset}
                                                className="col-span-2 text-xs text-gray-500 hover:text-gray-300 py-2 underline decoration-gray-700 hover:decoration-gray-500"
                                            >
                                                Start New Backtest
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Error Message */}
                        {error && status !== 'FAILED' && (
                            <div className="bg-red-500/10 border border-red-500/20 p-4 rounded-xl flex items-start gap-3">
                                <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                                <div>
                                    <h4 className="text-red-400 font-medium text-sm">System Error</h4>
                                    <p className="text-red-300/80 text-xs mt-1 leading-relaxed">{error}</p>
                                </div>
                            </div>
                        )}

                    </div>

                    {/* RIGHT PANEL: History Table (8 cols) */}
                    <div className="col-span-12 lg:col-span-8 h-full bg-[#1a1d29] rounded-2xl border border-gray-800 shadow-xl flex flex-col overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-800 flex justify-between items-center bg-[#1a1d29]">
                            <h3 className="text-sm font-bold text-gray-200 uppercase tracking-wider">Backtest History</h3>
                            <button
                                onClick={fetchJobs}
                                className={`p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-all ${isRefreshing ? 'animate-spin text-blue-400' : ''}`}
                                title="Refresh History"
                            >
                                <RefreshCw className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-auto custom-scrollbar p-0">
                            <table className="w-full text-left border-collapse">
                                <thead className="bg-[#14161f] text-xs uppercase text-gray-500 font-semibold sticky top-0 z-10 shadow-sm">
                                    <tr>
                                        <th className="px-6 py-3 border-b border-gray-800">Timestamp</th>
                                        <th className="px-6 py-3 border-b border-gray-800">Strategy File</th>
                                        <th className="px-6 py-3 border-b border-gray-800">Status</th>
                                        <th className="px-6 py-3 border-b border-gray-800 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-800/50">
                                    {jobs.length === 0 ? (
                                        <tr>
                                            <td colSpan="4" className="text-center py-20">
                                                <div className="flex flex-col items-center gap-3 opacity-50">
                                                    <FileCode className="w-10 h-10 text-gray-600" />
                                                    <span className="text-gray-500 text-sm">No history available</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        jobs.map((job) => (
                                            <tr key={job.id} className="hover:bg-[#202330] transition-colors group">
                                                <td className="px-6 py-4 text-sm text-gray-400 whitespace-nowrap font-mono">
                                                    {formatDateTime(job.created_at)}
                                                </td>
                                                <td className="px-6 py-4 text-sm text-white font-medium">
                                                    <div className="flex items-center gap-2">
                                                        <FileCode className="w-3.5 h-3.5 text-blue-500/70" />
                                                        {job.original_filename || "Unknown"}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wide uppercase shadow-sm
                                                        ${job.status === 'COMPLETED' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' :
                                                            job.status === 'FAILED' ? 'bg-red-500/10 text-red-400 border border-red-500/20' :
                                                                'bg-blue-500/10 text-blue-400 border border-blue-500/20 animate-pulse'}`}>
                                                        <span className={`w-1.5 h-1.5 rounded-full ${job.status === 'COMPLETED' ? 'bg-emerald-400' :
                                                            job.status === 'FAILED' ? 'bg-red-400' : 'bg-blue-400'
                                                            }`}></span>
                                                        {job.status}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <div className="flex justify-end gap-2 transition-opacity">
                                                        {(job.status === 'UPLOADING' || job.status === 'PROCESSING' || job.status === 'QUEUED') ? (
                                                            <button
                                                                onClick={() => {
                                                                    setJobId(job.id);
                                                                    setStatus('PROCESSING');
                                                                }}
                                                                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600/10 text-blue-400 hover:bg-blue-600 hover:text-white rounded-md text-xs font-medium transition-all"
                                                            >
                                                                <Loader2 className="w-3 h-3" /> Monitor
                                                            </button>
                                                        ) : (
                                                            <>
                                                                {job.status === 'COMPLETED' && (
                                                                    <a
                                                                        href={`${BACKTEST_API_URL}/job/${job.id}/result`}
                                                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600/10 text-emerald-400 hover:bg-emerald-600 hover:text-white rounded-md text-xs font-medium transition-all"
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                    >
                                                                        <Download className="w-3 h-3" /> Report
                                                                    </a>
                                                                )}
                                                                <button
                                                                    onClick={(e) => handleDeleteJob(job.id, e)}
                                                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600/10 text-red-400 hover:bg-red-600 hover:text-white rounded-md text-xs font-medium transition-all"
                                                                    title="Delete Job"
                                                                >
                                                                    <Trash2 className="w-3 h-3" />
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                </div>
            </div>
            {/* Delete Confirmation Modal */}
            {
                jobToDelete && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                        <div
                            className="bg-[#1a1d29] border border-gray-800 rounded-xl p-6 w-full max-w-sm shadow-2xl transform transition-all scale-100"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex flex-col items-center text-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mb-2">
                                    <AlertCircle className="w-6 h-6 text-red-500" />
                                </div>

                                <div>
                                    <h3 className="text-lg font-bold text-white mb-1">Delete Backtest?</h3>
                                    <p className="text-sm text-gray-400">
                                        Are you sure you want to delete this job? This action cannot be undone.
                                    </p>
                                </div>

                                <div className="flex gap-3 w-full mt-2">
                                    <button
                                        onClick={() => setJobToDelete(null)}
                                        className="flex-1 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg text-sm font-medium transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={confirmDelete}
                                        className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors shadow-lg shadow-red-900/20"
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }
        </div >
    );
};

export default BacktestView;
